import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GuidaLayout, Blocco, Passi, L, A } from '../components/GuidaUI';
import {
  BookOpen, Check, Sun, CalendarRange, Clock, Clapperboard, LifeBuoy, Star, BookMarked,
  Route, Compass, Rocket, ArrowRight,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Guida per Social Media Manager: onboarding, come muoversi in EdenFlow, routine e problemi frequenti.
 * Contenuto statico (si aggiorna qui nel codice). La checklist del primo giorno si ricorda per utente.
 */

const SEZIONI = [
  { id: 'inizio', label: 'Primo giorno', Icon: Rocket },
  { id: 'mappa', label: 'Dove trovi cosa', Icon: Compass },
  { id: 'ciclo', label: 'La vita di un contenuto', Icon: Route },
  { id: 'giornata', label: 'La tua giornata', Icon: Sun },
  { id: 'mese', label: 'Settimana e mese', Icon: CalendarRange },
  { id: 'storie', label: 'Storie', Icon: Clock },
  { id: 'video', label: 'Con il videomaker', Icon: Clapperboard },
  { id: 'problemi', label: 'Se qualcosa va storto', Icon: LifeBuoy },
  { id: 'regole', label: 'Regole d\'oro', Icon: Star },
  { id: 'glossario', label: 'Glossario', Icon: BookMarked },
];

const CHECKLIST = [
  { k: 'spazio', testo: <>Scegli lo spazio <b>Social</b> (in alto nella barra a sinistra; da telefono in <b>Menu</b>): il menu mostra solo quello che ti serve.</> },
  { k: 'lavoro', testo: <>Apri <L to="/">Il mio lavoro</L>: è la tua pagina di partenza ogni giorno.</> },
  { k: 'clienti', testo: <>Guarda i <L to="/clienti-attivi">clienti attivi</L> e chiedi a chi ti affianca quali segui tu.</> },
  { k: 'voce', testo: <>Per ogni tuo cliente apri il <L to="/ped">Piano Editoriale</L> → menu <b>⋯</b> → <b>Voce del brand</b>: tono, parole vietate, hashtag.</> },
  { k: 'rubriche', testo: <>Sempre dal menu <b>⋯</b>, apri <b>Rubriche</b>: sono i format che il cliente ha ogni settimana.</> },
  { k: 'contenuto', testo: <>Apri un contenuto qualsiasi e leggi il riquadro <b>Prossimo passo</b>: ti dice sempre cosa manca.</> },
  { k: 'pubblicazioni', testo: <>Apri <L to="/pubblicazioni">Pubblicazioni</L> e guarda se ci sono errori in rosso: impara a riconoscerli.</> },
  { k: 'storie', testo: <>Dai un'occhiata alle <L to="/ped-storie">Storie</L> e alla differenza tra Auto e Manuale (vedi sotto).</> },
  { k: 'leggi', testo: <>Leggi fino in fondo <A href="#problemi">Se qualcosa va storto</A> e le <A href="#regole">Regole d'oro</A>.</> },
];

const TAPPE = [
  { t: 'Idea', chi: 'SMM', d: 'Titolo, formato, piattaforma e data nel Piano Editoriale.' },
  { t: 'Copy', chi: 'SMM', d: 'Hook e caption. Quando il testo è definitivo: "Copy pronto".' },
  { t: 'Visual', chi: 'Videomaker / grafico', d: 'Foto, grafica o video. Quando è fatto: "Visual pronto".' },
  { t: 'File', chi: 'Chi ha fatto il visual', d: 'Il file finale caricato nel contenuto (sezione File e media).' },
  { t: 'Approvato', chi: 'SMM', d: 'Controlli anteprima, testo e file, poi "Approva".' },
  { t: 'Programmato', chi: 'SMM', d: '"Programma su PostProxy": uscirà da solo alla data e ora previste.' },
  { t: 'Online', chi: 'Automatico', d: 'Pubblicato. I numeri (reach, like…) arrivano da soli.' },
];

const PROBLEMI = [
  ['Un contenuto è in "Da risolvere"',
    'Vuol dire che la data è passata senza che fosse programmato, oppure che la pubblicazione è fallita. Aprilo: il Prossimo passo ti dice quale dei due e cosa fare.'],
  ['"Pubblicazione non riuscita"',
    'Nella sezione Pubblicazione del contenuto trovi l\'errore. Di solito è un file mancante o non valido, una data passata o il profilo social non collegato. Correggi e riprogramma. Se non capisci l\'errore, chiedi prima di riprovare più volte.'],
  ['La data è passata e non posso programmare',
    'PostProxy non accetta date nel passato. Sposta la data in avanti, salva e programma.'],
  ['Il cliente ha chiesto modifiche',
    'Lo vedi in "Da risolvere" e nella scheda del contenuto, con il suo commento. Fai le modifiche, salva e avvisa chi segue il cliente.'],
  ['Non vedo il pulsante "Programma" o "Approva"',
    'Dipende dal ruolo con cui sei entrato: approvare e programmare spetta al social media manager. Se ti serve, chiedi all\'admin di controllare il tuo ruolo.'],
  ['Ho programmato per sbaglio',
    '"Azzera stato" nella sezione Pubblicazione azzera lo stato in EdenFlow ma NON cancella il post su PostProxy. Avvisa subito chi gestisce PostProxy per toglierlo dalla coda.'],
  ['Ho modificato ma non vedo i cambiamenti',
    'Controlla in alto se c\'è scritto "Modifiche non salvate": premi Salva (o Ctrl+S). Copy pronto, Visual pronto e Approva invece si salvano da soli.'],
];

const REGOLE = [
  'Ogni post, reel, carosello o storia è UN contenuto nel Piano Editoriale. Se non è lì, non esiste.',
  'Mai pubblicare a mano qualcosa che è già programmato: uscirebbe due volte.',
  'Ogni contenuto ha sempre una data, anche provvisoria.',
  'Prima di approvare guarda l\'Anteprima: è quello che vedranno le persone.',
  'La caption segue la Voce del brand: se compare un avviso su una parola vietata, cambiala.',
  'Ogni mattina, prima di tutto, svuota "Da risolvere".',
  'Nel dubbio, chiedi: un post sbagliato online costa più di una domanda.',
];

const GLOSSARIO = [
  ['PED', 'Piano Editoriale: l\'elenco di tutti i contenuti di un cliente, con data e stato.'],
  ['Hook', 'La frase d\'apertura che ferma lo scroll (i primi 1–3 secondi o la prima riga).'],
  ['Copy / caption', 'Il testo del post. "Copy pronto" = testo definitivo.'],
  ['Visual', 'La parte visiva: foto, grafica o video. "Visual pronto" = fatto.'],
  ['Rubrica', 'Un format che si ripete (es. "Consiglio del lunedì"): con un clic genera i suoi contenuti del mese.'],
  ['Voce del brand', 'Tono, parole da evitare, hashtag e frasi pronte di un cliente.'],
  ['Contratto', 'Quanti contenuti il cliente ha comprato al mese: il numero rosso accanto al cliente dice quanti ne mancano.'],
  ['PostProxy', 'Il servizio che pubblica i post sui social alla data e ora previste.'],
  ['Portale cliente', 'Il link privato dove il cliente vede i contenuti pronti, li approva o chiede modifiche.'],
  ['Review cliente', 'La fase in cui il video è visibile al cliente nel portale.'],
];

function Mappa() {
  const voci = [
    ['/', 'Il mio lavoro', 'La tua bacheca: cosa esce a breve, le tue task e i contenuti divisi per cosa manca. Si parte sempre da qui.'],
    ['/ped', 'Piano Editoriale', 'Tutti i post, reel e caroselli di un cliente, per mese. Da qui crei contenuti, scarichi il PDF per il cliente, copi il mese.'],
    ['/produzione', 'Produzione video', 'I video in lavorazione con il videomaker: fasi, versioni, commenti.'],
    ['/ped-storie', 'Storie', 'Le storie di ogni cliente con il loro orario.'],
    ['/trend', 'Trend', 'La bacheca dei trend del team: incolli un link, il video si scarica da solo, e con un clic diventa un contenuto del piano.'],
    ['/pubblicazioni', 'Pubblicazioni', 'Cosa esce e com\'è andata: errori, in arrivo, pubblicati con i numeri.'],
    ['/calendario', 'Calendario', 'Tutto nel calendario: contenuti, riprese, scadenze.'],
    ['/task', 'Task', 'Le cose da fare assegnate a te.'],
    ['/analytics', 'Analytics', 'Follower e risultati dei profili dei clienti.'],
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
      {voci.map(([to, t, d]) => (
        <Link key={to} to={to} style={{ textDecoration: 'none', padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg2)', display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 650, color: 'var(--text)' }}>{t}<ArrowRight size={13} color="var(--text-dim)" /></div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.45 }}>{d}</div>
        </Link>
      ))}
    </div>
  );
}

function Ciclo() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
      {TAPPE.map((t, i) => (
        <div key={t.t} style={{ padding: '12px', borderRadius: 12, background: 'var(--bg2)', border: '1px solid var(--border)', position: 'relative' }}>
          <div style={{ height: 3, borderRadius: 3, background: i === TAPPE.length - 1 ? 'var(--green)' : 'var(--cyan)', opacity: 0.35 + (i / TAPPE.length) * 0.65, marginBottom: 10 }} />
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-dim)' }}>{i + 1}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{t.t}</div>
          <div style={{ fontSize: 11.5, color: 'var(--cyan)', fontWeight: 600, margin: '2px 0 6px' }}>{t.chi}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>{t.d}</div>
        </div>
      ))}
    </div>
  );
}

function Checklist({ userId }) {
  const key = `eden-guida-checklist:${userId || 'anon'}`;
  const [fatti, setFatti] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key)) || {}; } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(fatti)); } catch {} }, [key, fatti]);
  const n = CHECKLIST.filter(c => fatti[c.k]).length;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ flex: 1, height: 6, borderRadius: 6, background: 'var(--border)', overflow: 'hidden' }}>
          <div style={{ width: `${(n / CHECKLIST.length) * 100}%`, height: '100%', background: 'var(--green)', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: n === CHECKLIST.length ? 'var(--green)' : 'var(--text-muted)' }}>{n}/{CHECKLIST.length}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {CHECKLIST.map(c => {
          const on = !!fatti[c.k];
          return (
            <label key={c.k} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
              background: on ? 'var(--green-dim)' : 'var(--bg2)', border: '1px solid var(--border)' }}>
              <input type="checkbox" checked={on} onChange={() => setFatti(f => ({ ...f, [c.k]: !f[c.k] }))}
                style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
              <span aria-hidden style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: `1.5px solid ${on ? 'var(--green)' : 'var(--border-h)'}`, background: on ? 'var(--green)' : 'transparent' }}>
                {on && <Check size={13} strokeWidth={3} color="var(--on-accent, #000)" />}
              </span>
              <span style={{ fontSize: 13.5, color: on ? 'var(--text-muted)' : 'var(--text)', textDecoration: on ? 'line-through' : 'none' }}>{c.testo}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default function Guida() {
  const { user } = useAuth();
  return (
    <GuidaLayout Icon={BookOpen} titolo="Guida per Social Media Manager" sezioni={SEZIONI}
      sottotitolo="Come si lavora in EdenFlow, dal primo giorno alla routine di tutti i giorni."
      azioni={<L to="/guida-trend">Guida ai trend →</L>}>
          <Blocco id="inizio" Icon={Rocket} titolo="Primo giorno" sotto="Una lista da spuntare: in un'ora sai muoverti da solo.">
            <Checklist userId={user?.id} />
          </Blocco>

          <Blocco id="mappa" Icon={Compass} titolo="Dove trovi cosa" sotto="Il menu dello spazio Social. Tocca una voce per aprirla.">
            <Mappa />
            <p style={{ margin: '14px 0 0' }}>
              Il <b>cliente</b> che scegli nel Piano Editoriale resta lo stesso anche in Storie e Campagne, e la volta dopo lo ritrovi già selezionato.
              Per cercare qualsiasi cosa usa la barra in alto o <b>Ctrl+K</b>.
            </p>
          </Blocco>

          <Blocco id="ciclo" Icon={Route} titolo="La vita di un contenuto" sotto="Ogni contenuto passa da queste tappe, sempre in quest'ordine.">
            <Ciclo />
            <p style={{ margin: '14px 0 0' }}>
              Non devi ricordarle: dentro ogni contenuto il riquadro <b>Prossimo passo</b> mostra la barra con le tappe fatte e ti dice
              l'unica cosa da fare adesso, con il pulsante per farla.
            </p>
          </Blocco>

          <Blocco id="giornata" Icon={Sun} titolo="La tua giornata" sotto="Una routine semplice che evita le sorprese.">
            <Passi items={[
              <>Apri <L to="/">Il mio lavoro</L> e guarda <b>In uscita nei prossimi 2 giorni</b>: è tutto pronto?</>,
              <>Svuota la colonna <b>Da risolvere</b> (scaduti, falliti, modifiche chieste dal cliente). Ha la precedenza su tutto.</>,
              <>Lavora la colonna <b>Copy da scrivere</b>: scrivi, poi "Copy pronto" direttamente dalla card.</>,
              <>Controlla <b>In attesa</b>: se un visual è in ritardo, scrivi al videomaker.</>,
              <>Prima di chiudere: <b>Da programmare</b> dovrebbe essere vuota. Programma quello che è pronto.</>,
              <>Una volta al giorno apri <L to="/pubblicazioni">Pubblicazioni</L> e verifica che non ci siano errori in rosso.</>,
            ]} />
          </Blocco>

          <Blocco id="mese" Icon={CalendarRange} titolo="Settimana e mese" sotto="Come si prepara il piano e come si mostra al cliente.">
            <Passi items={[
              <><b>Pianifica il mese prima che inizi.</b> Nel <L to="/ped">Piano Editoriale</L> scegli il mese: puoi partire dal mese prima con
                <b> ⋯ → Copia mese sul mese dopo</b> (le copie ripartono da Idea), oppure aprire <b>⋯ → Rubriche</b> e generare con un clic i contenuti ricorrenti del mese.</>,
              <><b>Controlla il contratto.</b> Il numero accanto al cliente (es. <b>−3</b>) dice quanti contenuti mancano rispetto a quelli che ha comprato.</>,
              <><b>Mostra il piano al cliente.</b> <b>⋯ → Scarica PDF per il cliente</b>: un PDF pulito con date, formati, titoli e hook, senza niente di interno.
                Mandalo prima di iniziare la produzione.</>,
              <><b>Approvazioni dei video.</b> Il cliente può approvare o chiedere modifiche dal suo <b>Portale cliente</b> (il link lo copi dalla scheda del cliente).
                I video compaiono lì solo quando sono in fase "Review cliente".</>,
              <><b>A fine mese</b> guarda <L to="/analytics">Analytics</L> e le <L to="/pubblicazioni">Pubblicazioni</L>: cosa ha funzionato va ripetuto.</>,
            ]} />
          </Blocco>

          <Blocco id="storie" Icon={Clock} titolo="Storie">
            <p style={{ margin: '0 0 10px' }}>Le storie vivono nella scheda <L to="/ped-storie">Storie</L>. Ognuna ha data, ora e modalità:</p>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li><b>Auto</b>: esce da sola via PostProxy, come i post.</li>
              <li><b>Link</b> o <b>Manuale</b>: va pubblicata a mano (es. con sticker link o sondaggi). Compare come promemoria in calendario: quando arriva l'ora, la pubblichi tu.</li>
            </ul>
            <p style={{ margin: '10px 0 0' }}>Se non metti un orario: le storie escono alle <b>18:00</b>, i post alle <b>12:00</b>.</p>
          </Blocco>

          <Blocco id="video" Icon={Clapperboard} titolo="Lavorare con il videomaker">
            <Passi items={[
              <>Nel contenuto, sezione <b>Team e produzione</b>: assegna te come responsabile e il videomaker.</>,
              <>Le fasi del video sono <b>Script → Pre-produzione → Riprese → Montaggio → Review interna → Review cliente → Approvato</b>.
                Le vedi tutte in <L to="/produzione">Produzione video</L>.</>,
              <>Quando il videomaker carica una versione, il video passa in <b>Review interna</b>: guardalo e lascia i commenti <b>al secondo esatto</b> (timecode).</>,
              <>Quando va bene per te, portalo in <b>Review cliente</b>: solo da lì il cliente lo vede nel suo portale.</>,
              <>Approvato il video, il file finale va nel contenuto e il resto del percorso è quello di sempre (approva → programma).</>,
            ]} />
          </Blocco>

          <Blocco id="problemi" Icon={LifeBuoy} titolo="Se qualcosa va storto">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PROBLEMI.map(([q, a]) => (
                <details key={q} style={{ borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg2)', padding: '11px 14px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 650, color: 'var(--text)', fontSize: 14 }}>{q}</summary>
                  <p style={{ margin: '8px 0 0', fontSize: 13.5 }}>{a}</p>
                </details>
              ))}
            </div>
          </Blocco>

          <Blocco id="regole" Icon={Star} titolo="Regole d'oro">
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {REGOLE.map(r => <li key={r} style={{ color: 'var(--text)' }}>{r}</li>)}
            </ol>
          </Blocco>

          <Blocco id="glossario" Icon={BookMarked} titolo="Glossario">
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(110px, 160px) 1fr', gap: '10px 16px' }}>
              {GLOSSARIO.map(([t, d]) => (
                <React.Fragment key={t}>
                  <dt style={{ fontWeight: 700, color: 'var(--text)' }}>{t}</dt>
                  <dd style={{ margin: 0 }}>{d}</dd>
                </React.Fragment>
              ))}
            </dl>
          </Blocco>
    </GuidaLayout>
  );
}
